import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { formatDistanceToNow } from 'date-fns';
import { 
  TaskComment, 
  TaskCommentCreate, 
  UserDetail 
} from '../../../models/task-management.models';

@Component({
  selector: 'app-task-comments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    DialogModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  template: `
    <p-dialog 
      header="Task Comments"
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="task-comments-dialog"
      [style]="{'width': '700px', 'max-width': '95vw', 'height': '600px'}"
      (onHide)="onHide()">
      
      <div class="comments-container">
        
        <!-- New Comment Form -->
        <div class="new-comment-form">
          <div class="comment-input-container">
            <textarea 
              #commentTextarea
              [(ngModel)]="newCommentText"
              placeholder="Add a comment..."
              class="comment-input"
              rows="3"
              maxlength="1000"
              (keydown.ctrl.enter)="submitComment()"
              (keydown.meta.enter)="submitComment()"></textarea>
            
            <div class="comment-actions">
              <div class="file-upload" *ngIf="allowFileUploads">
                <input 
                  #fileInput
                  type="file"
                  multiple
                  [accept]="allowedFileTypes"
                  (change)="onFilesSelected($event)"
                  style="display: none;" />
                <p-button 
                  icon="pi pi-paperclip"
                  [text]="true"
                  size="small"
                  pTooltip="Attach files"
                  (click)="fileInput.click()" />
              </div>
              
              <div class="submit-actions">
                <span class="char-count">{{ newCommentText.length }}/1000</span>
                <p-button 
                  label="Comment"
                  icon="pi pi-send"
                  size="small"
                  (click)="submitComment()"
                  [disabled]="!newCommentText.trim() || submittingComment"
                  [loading]="submittingComment" />
              </div>
            </div>
          </div>
          
          <!-- Selected Files Preview -->
          <div class="selected-files" *ngIf="selectedFiles.length > 0">
            <div class="files-header">
              <span>Attachments ({{ selectedFiles.length }})</span>
              <p-button 
                icon="pi pi-times"
                [text]="true"
                size="small"
                (click)="clearSelectedFiles()" />
            </div>
            <div class="file-list">
              <div class="file-item" *ngFor="let file of selectedFiles; trackBy: trackByFileName">
                <i class="pi pi-file file-icon"></i>
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">({{ formatFileSize(file.size) }})</span>
                <p-button 
                  icon="pi pi-times"
                  [text]="true"
                  size="small"
                  (click)="removeFile(file)" />
              </div>
            </div>
          </div>
        </div>

        <!-- Comments List -->
        <div class="comments-list" [class.empty]="!comments.length">
          <div class="empty-state" *ngIf="!comments.length">
            <i class="pi pi-comment"></i>
            <h3>No comments yet</h3>
            <p>Be the first to comment on this task</p>
          </div>

          <div class="comment-item" *ngFor="let comment of comments; trackBy: trackByCommentId">
            <div class="comment-avatar">
              <div class="avatar-circle">
                {{ getInitials(comment.createdByName) }}
              </div>
            </div>
            
            <div class="comment-content">
              <div class="comment-header">
                <span class="comment-author">{{ comment.createdByName || 'Unknown' }}</span>
                <span class="comment-date" [pTooltip]="formatFullDate(comment.createdAt)">
                  {{ formatRelativeDate(comment.createdAt) }}
                </span>
                
                <div class="comment-actions" *ngIf="canDeleteComment(comment)">
                  <p-button 
                    icon="pi pi-trash"
                    [text]="true"
                    size="small"
                    severity="danger"
                    pTooltip="Delete comment"
                    (click)="deleteComment(comment)" />
                </div>
              </div>
              
              <div class="comment-body">
                <p>{{ comment.commentText }}</p>
              </div>
              
              <!-- Comment Attachments -->
              <div class="comment-attachments" *ngIf="comment.attachments?.length">
                <div class="attachment-item" *ngFor="let attachment of comment.attachments">
                  <i class="pi pi-file"></i>
                        <a [href]="attachment.fileUrl" target="_blank" class="attachment-link">
                    {{ attachment.fileName }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <ng-template pTemplate="footer">
        <div class="dialog-footer">
          <p-button 
            label="Close" 
            icon="pi pi-times"
            [text]="true"
            (click)="onHide()" />
        </div>
      </ng-template>
    </p-dialog>

    <p-confirmDialog />
  `,
  styles: [`
    .comments-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: 500px;
    }

    .new-comment-form {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 16px;
      margin-bottom: 16px;
    }

    .comment-input-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .comment-input {
      width: 100%;
      min-height: 80px;
      padding: 12px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-family: inherit;
      font-size: 14px;
      resize: vertical;
      transition: border-color 0.2s ease;
    }

    .comment-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px #3b82f6;
    }

    .comment-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .submit-actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .char-count {
      font-size: 12px;
      color: #6b7280;
    }

    .selected-files {
      margin-top: 12px;
      padding: 12px;
      background-color: #f9fafb;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }

    .files-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
    }

    .file-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .file-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background-color: white;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
    }

    .file-icon {
      color: #6b7280;
    }

    .file-name {
      flex: 1;
      font-size: 14px;
      color: #374151;
      truncate: true;
    }

    .file-size {
      font-size: 12px;
      color: #6b7280;
    }

    .comments-list {
      flex: 1;
      overflow-y: auto;
      padding-right: 4px;
    }

    .comments-list.empty {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #6b7280;
    }

    .empty-state i {
      font-size: 48px;
      color: #d1d5db;
      margin-bottom: 16px;
    }

    .empty-state h3 {
      margin: 0 0 8px 0;
      font-size: 18px;
      color: #374151;
    }

    .empty-state p {
      margin: 0;
      font-size: 14px;
    }

    .comment-item {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
    }

    .comment-avatar {
      flex-shrink: 0;
    }

    .avatar-circle {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: #3b82f6;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
    }

    .comment-content {
      flex: 1;
      min-width: 0;
    }

    .comment-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
    }

    .comment-author {
      font-weight: 600;
      color: #374151;
      font-size: 14px;
    }

    .comment-date {
      font-size: 12px;
      color: #6b7280;
    }

    .comment-actions {
      margin-left: auto;
    }

    .comment-body {
      color: #374151;
      line-height: 1.5;
      font-size: 14px;
      word-wrap: break-word;
    }

    .comment-body p {
      margin: 0;
      white-space: pre-wrap;
    }

    .comment-attachments {
      margin-top: 12px;
      padding: 8px;
      background-color: #f9fafb;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
    }

    .attachment-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
    }

    .attachment-link {
      color: #3b82f6;
      text-decoration: none;
      font-size: 14px;
    }

    .attachment-link:hover {
      text-decoration: underline;
    }

    .dialog-footer {
      display: flex;
      justify-content: flex-end;
    }

    /* Scrollbar styling */
    .comments-list::-webkit-scrollbar {
      width: 6px;
    }

    .comments-list::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }

    .comments-list::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }

    .comments-list::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }

    @media (max-width: 768px) {
      .comment-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
      }

      .submit-actions {
        justify-content: space-between;
      }

      .comment-header {
        flex-wrap: wrap;
      }

      .comment-actions {
        margin-left: 0;
        margin-top: 4px;
      }

      .file-item {
        flex-wrap: wrap;
      }

      .file-name {
        flex-basis: 100%;
        margin-left: 24px;
      }
    }
  `]
})
export class TaskCommentsComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() taskId!: number;
  @Input() comments: TaskComment[] = [];
  @Input() currentUser?: UserDetail;
  @Input() allowFileUploads: boolean = true;
  @Input() allowedFileTypes: string = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif';
  @Input() maxFileSize: number = 10 * 1024 * 1024; // 10MB
  @Input() submittingComment: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() commentSubmit = new EventEmitter<TaskCommentCreate>();
  @Output() commentDelete = new EventEmitter<number>();
  @Output() hide = new EventEmitter<void>();

  @ViewChild('commentTextarea') commentTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  newCommentText: string = '';
  selectedFiles: File[] = [];

  constructor(private confirmationService: ConfirmationService) {}

  ngOnInit() {
    // Auto-focus comment textarea when dialog opens
    if (this.visible) {
      setTimeout(() => {
        this.commentTextarea?.nativeElement?.focus();
      }, 300);
    }
  }

  onHide() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.hide.emit();
    this.clearForm();
  }

  submitComment() {
    if (!this.newCommentText.trim()) {
      return;
    }

    const commentData: TaskCommentCreate = {
      taskId: this.taskId,
      commentText: this.newCommentText.trim(),
      files: this.selectedFiles
    };

    this.commentSubmit.emit(commentData);
    this.clearForm();
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files).filter(file => {
        if (file.size > this.maxFileSize) {
          // Could emit an error event here
          console.warn(`File ${file.name} is too large (${this.formatFileSize(file.size)}). Maximum size is ${this.formatFileSize(this.maxFileSize)}.`);
          return false;
        }
        return true;
      });

      this.selectedFiles = [...this.selectedFiles, ...newFiles];
      
      // Clear the input
      input.value = '';
    }
  }

  removeFile(fileToRemove: File) {
    this.selectedFiles = this.selectedFiles.filter(file => file !== fileToRemove);
  }

  clearSelectedFiles() {
    this.selectedFiles = [];
  }

  deleteComment(comment: TaskComment) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this comment?',
      header: 'Delete Comment',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.commentDelete.emit(comment.commentId);
      }
    });
  }

  canDeleteComment(comment: TaskComment): boolean {
    return !!(this.currentUser && 
             (this.currentUser.userId === comment.createdBy || 
              this.currentUser.role === 'Admin'));
  }

  getInitials(name?: string): string {
    if (!name) return '?';
    
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  formatRelativeDate(date: string | Date): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch {
      return 'Unknown date';
    }
  }

  formatFullDate(date: string | Date): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString();
    } catch {
      return 'Unknown date';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  trackByCommentId(index: number, comment: TaskComment): number {
    return comment.commentId;
  }

  trackByFileName(index: number, file: File): string {
    return file.name + file.size + file.lastModified;
  }

  private clearForm() {
    this.newCommentText = '';
    this.selectedFiles = [];
  }
}